import { getRacerIdentity } from '../game/racer-config'
import type { SimulationState, Vec2 } from '../game/types'

export interface MinimapPoint {
  x: number
  y: number
}

export interface MinimapMarker extends MinimapPoint {
  id: string
  accent: string
  heading: number
  isPlayer: boolean
}

export interface MinimapModel {
  width: number
  height: number
  trackPoints: MinimapPoint[]
  start: MinimapPoint
  nextCheckpoint: MinimapPoint
  markers: MinimapMarker[]
}

function isFinitePoint(point: Vec2 | undefined): point is Vec2 {
  return Boolean(point && Number.isFinite(point.x) && Number.isFinite(point.y))
}

export function createMinimapModel(state: SimulationState, width: number, height: number): MinimapModel | null {
  if (width <= 0 || height <= 0 || state.track.samples.length < 4) return null
  const positions = state.track.samples.map((sample) => sample.position)
  if (!positions.every(isFinitePoint)) return null
  const xs = positions.map((position) => position.x)
  const ys = positions.map((position) => position.y)
  const minimumX = Math.min(...xs)
  const maximumX = Math.max(...xs)
  const minimumY = Math.min(...ys)
  const maximumY = Math.max(...ys)
  const worldWidth = maximumX - minimumX
  const worldHeight = maximumY - minimumY
  if (!Number.isFinite(worldWidth) || !Number.isFinite(worldHeight) || worldWidth <= 0 || worldHeight <= 0) return null

  const padding = 8
  const scale = Math.min((width - padding * 2) / worldWidth, (height - padding * 2) / worldHeight)
  if (!Number.isFinite(scale) || scale <= 0) return null
  const drawnWidth = worldWidth * scale
  const drawnHeight = worldHeight * scale
  const offsetX = (width - drawnWidth) * 0.5
  const offsetY = (height - drawnHeight) * 0.5
  const project = (point: Vec2): MinimapPoint => ({
    x: offsetX + (point.x - minimumX) * scale,
    y: height - (offsetY + (point.y - minimumY) * scale),
  })

  const startIndex = state.track.checkpoints[0] ?? 0
  const nextCheckpointIndex = state.race.progress[state.race.playerId]?.nextCheckpoint ?? 0
  const nextSampleIndex = state.track.checkpoints[nextCheckpointIndex] ?? startIndex
  const startPosition = state.track.samples[startIndex]?.position
  const checkpointPosition = state.track.samples[nextSampleIndex]?.position
  if (!isFinitePoint(startPosition) || !isFinitePoint(checkpointPosition)) return null

  return {
    width,
    height,
    trackPoints: positions.map(project),
    start: project(startPosition),
    nextCheckpoint: project(checkpointPosition),
    markers: state.vehicles
      .filter((vehicle) => isFinitePoint(vehicle.position))
      .map((vehicle) => ({
        ...project(vehicle.position),
        id: vehicle.id,
        accent: getRacerIdentity(vehicle.id).accent,
        heading: vehicle.heading,
        isPlayer: vehicle.id === state.race.playerId,
      })),
  }
}
